import { useMediaQuery } from '@abyss/web/hooks/useMediaQuery';
import { Button } from '@abyss/web/ui/Button';
import { IconSymbol } from '@abyss/web/ui/IconSymbol';
import { Text } from '@abyss/web/ui/Text';
import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { ProviderLocation } from '../../models/ProviderDetails';
import { getProviderTypeFromProviderSummaryType } from '../../utils/providerDetails.utils';
import { adobeLinkTrackEvent } from '../AdobeTagging/adobeLinkTrackEvent';
import { phoneOnly } from '../ConstantsStyles';
import { ContentWrapper } from '../ContentWrapper';
import { ContainerForAllSectionsStyled } from '../ProviderDetailTabs/ProviderDetailTabs.styled';
import { SendFeedbackFormDrawer } from '../SendFeedbackFormDrawer';

interface ProviderData {
  name: string | undefined;
  address: Record<string, any>;
  npi: string | undefined;
  mpin: string | undefined;
  providerType: string;
}

interface Props {
  data: ProviderData | null;
  type: string;
  coverageType: string;
  enableNewFeedbackFormDesign?: boolean;
  selectedIndex?: number;
  providerLocations?: ProviderLocation[];
}
export const SendFeedback = ({
  type = '',
  data,
  coverageType = '',
  enableNewFeedbackFormDesign,
  selectedIndex,
  providerLocations,
}: Props) => {
  const { t } = useTranslation();
  const [isOpen, setIsOpen] = useState(false);
  const mobileScreen = useMediaQuery(phoneOnly);
  useEffect(() => {
    if (isOpen) {
      const linkName: string = 'Send-FeedBack';
      const linkLocation: string =
        `${providerData.providerType} details`.replaceAll('_', ' ');
      adobeLinkTrackEvent({
        name: linkName,
        location: linkLocation,
        type: 'internal',
      });
    }
  }, [isOpen && providerLocations]);

  const { name, address, npi, mpin, providerType = '' } = data || {};

  const location: string =
    `${address?.line?.[0]}, ${address?.city}, ${address?.state} ${address?.postalCode}` ||
    '';

  const providerData = {
    name,
    npi,
    location,
    mpin,
    providerType: getProviderTypeFromProviderSummaryType(providerType),
  };

  return (
    <React.Fragment>
      <ContainerForAllSectionsStyled
        css={
          !enableNewFeedbackFormDesign
            ? {
                backgroundColor: '#F4F6FA',
                paddingTop: '18px',
                borderBottom: 0,
                '& > div': {
                  paddingBottom: 0,
                },
              }
            : { border: 'none' }
        }
      >
        <ContentWrapper
          style={
            enableNewFeedbackFormDesign
              ? { display: 'flex', alignItems: 'center', padding: 0, margin: 0 }
              : {}
          }
        >
          <Text
            css={
              enableNewFeedbackFormDesign
                ? {
                    display: 'flex',
                    fontWeight: mobileScreen ? 600 : 700,
                    fontSize: mobileScreen ? 14 : 16,
                    marginRight: 8,
                  }
                : { display: 'block', fontWeight: 700 }
            }
          >
            {enableNewFeedbackFormDesign
              ? t('FEEDBACK_FORM.OUT_OF_DATE_INFO_NEW_DESIGN')
              : t('FEEDBACK_FORM.OUT_OF_DATE_INFO')}
          </Text>
          {enableNewFeedbackFormDesign ? (
            <Button
              before={<IconSymbol icon="flag" size="20px" />}
              color={mobileScreen ? '$interactive1' : '$primary1'}
              css={{
                'abyss-button-root': {
                  paddingLeft: '$sm',
                  paddingRight: '$sm',
                },
                'abyss-insert-element-before': {
                  paddingRight: '0px !important',
                },
              }}
              data-testid="send-feedback-button"
              onClick={() => setIsOpen(true)}
              size={mobileScreen ? '$md' : '$sm'}
              variant={mobileScreen ? 'ghost' : 'outline'}
            >
              <Text
                color={mobileScreen ? '$interactive1' : '$primary1'}
                css={{
                  fontWeight: mobileScreen ? 600 : 700,
                  fontSize: 14,
                  marginLeft: 4,
                }}
              >
                {t('FEEDBACK_FORM.SEND_FEEDBACK')}
              </Text>
            </Button>
          ) : (
            <Button
              aria-label="send-feedback"
              css={{ marginTop: '8px' }}
              data-testid="send-feedback-button"
              onClick={() => setIsOpen(true)}
              size="$md"
              variant="outline"
            >
              {t('FEEDBACK_FORM.SEND_FEEDBACK')}
            </Button>
          )}
        </ContentWrapper>
      </ContainerForAllSectionsStyled>
      {isOpen && (
        <SendFeedbackFormDrawer
          coverageType={coverageType}
          enableNewFeedbackFormDesign={enableNewFeedbackFormDesign}
          isOpen={isOpen}
          providerData={providerData}
          providerLocations={providerLocations}
          selectedIndex={selectedIndex}
          setIsOpen={setIsOpen}
          type={type}
        />
      )}
    </React.Fragment>
  );
};
